import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import StandupListItem from '../components/StandupListItem';
import StandupListItemSkeleton from '../components/StandUpSkeletons/StandupListItemSkeleton';
import { StandupCardItem, StandUpCardsScreenProps } from '../types';
import { useStandUpCardsFilters } from '../hooks/useStandUpCardsFilters';
import { useStandUpCardsData } from '../hooks/useStandUpCardsData';
import { formatModifiedDate } from '../hooks/useStandUpDashBoard';
import FontFamily from '../../../constants/fonts';
import { formatUserName } from '../../../utils/emailTouserName';
import { useSelectedLocation } from '../../../hooks';
import { setToastNotification } from '../../../store/slices/standUpSlice';
import LHFilter, { FilterItem, DropDownOption } from '../../../components/LHFilter';
import StandupStatsCard from '../components/charts/StandupStatsCard';

const StandUpCardsScreen: React.FC<StandUpCardsScreenProps> = ({ onBackPress, items }) => {
  const dispatch = useDispatch();
  const selectedLocation = useSelectedLocation();
  const userRoleId = useSelector(
    (state: RootState) => state.appData?.userInfo?.roleId ?? state.auth?.user?.roleId ?? null
  );
  const [filteredItems, setFilteredItems] = useState<StandupCardItem[]>(items);
  const [originalItems, setOriginalItems] = useState<StandupCardItem[]>(items);
  
  // Use filters hook
  const { filters, handleFilterChange, handleResetFilters } = useStandUpCardsFilters();
  
  // Use data fetching hook
  const { isLoading, fetchFilteredData } = useStandUpCardsData({
    filters,
    selectedLocation,
    userRoleId,
  });

  // Update original items when items prop changes
  useEffect(() => {
    setOriginalItems(items);
    setFilteredItems(items);
  }, [items]);

  // Helper function to extract date from scheduled_date
  const getItemDate = (item: StandupCardItem) => {
    let dateString = '';
    
    // Get the date string from various possible fields
    if ((item as any).scheduled_date) {
      dateString = (item as any).scheduled_date;
    } else if (item.date) {
      dateString = item.date;
    } else if (item.DATE_RANGE) {
      dateString = item.DATE_RANGE;
    } else if ((item as any).created_at) {
      dateString = (item as any).created_at;
    } else if (item.CREATED_AT) {
      dateString = item.CREATED_AT;
    }
    
    if (!dateString) return '';
    
    // If already in MM-DD-YYYY format, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse YYYY-MM-DD format directly to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${month}-${day}-${year}`;
    }
    
    // Try to parse and format the date (for other formats)
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateString;
    }
  };

  // Helper function to get standup type
  const getItemType = (item: StandupCardItem) => {
    // API returns standup_type
    return (item as any).standup_type || item.type || item.STANDUP_TYPE || '';
  };

  // Helper function to get status
  const getItemStatus = (item: StandupCardItem) => {
    // API returns status (lowercase)
    return (item as any).status || item.STATUS || '';
  };

  // Helper function to get created by
  const getItemCreatedBy = (item: StandupCardItem) => {
    // API returns created_by
    return (item as any).created_by || item.createdBy || item.CREATED_BY || '';
  };

  // Helper function to get modified by
  const getItemModifiedBy = (item: StandupCardItem) => {
    // API returns modified_by
    return (item as any).modified_by || item.modifiedBy || item.MODIFIED_BY || '';
  };

  // Build filter options matching SparcWalk pattern
  // Use filteredItems so filter options update based on current filters
  const filterOptions: FilterItem[] = [
    {
      key: 'date',
      label: 'Date',
      selectedOptionId: filters.date,
      options: Array.from(
        new Set(
          filteredItems
            .map(item => getItemDate(item))
            .filter(date => date)
        )
      ).map(date => ({
        id: date,
        displayText: date,
      })),
    },
    {
      key: 'standupType',
      label: 'Stand up Type',
      selectedOptionId: filters.standupType,
      options: Array.from(
        new Set(
          filteredItems
            .map(item => getItemType(item))
            .filter(type => type)
        )
      ).map(type => ({
        id: type,
        displayText: type.replace(/_/g, ' ').split(' ').map((word: string, index: number) => 
          index === 0 
            ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() 
            : word.toUpperCase()
        ).join(' '),
      })),
    },
    {
      key: 'status',
      label: 'Status',
      selectedOptionId: filters.status,
      options: Array.from(
        new Set(
          filteredItems
            .map(item => getItemStatus(item))
            .filter(status => status)
        )
      ).map(status => ({
        id: status,
        displayText: status,
      })),
    },
    {
      key: 'createdBy',
      label: 'Created By',
      selectedOptionId: filters.createdBy,
      options: Array.from(
        new Set(
          filteredItems
            .map(item => getItemCreatedBy(item))
            .filter(creator => creator)
        )
      ).map(creator => ({
        id: creator, // Keep email as id for API
        displayText: formatUserName(creator), // Display name
        email: creator, // Store email for reference
      })),
    },
    {
      key: 'modifiedBy',
      label: 'Modified By',
      selectedOptionId: filters.modifiedBy,
      options: Array.from(
        new Set(
          filteredItems
            .map(item => getItemModifiedBy(item))
            .filter(modifier => modifier)
        )
      ).map(modifier => ({
        id: modifier, // Keep email as id for API
        displayText: formatUserName(modifier), // Display name
        email: modifier, // Store email for reference
      })),
    },
  ];

  // Apply filters when they change
  useEffect(() => {
    const hasActiveFilters = Object.values(filters).some(value => value !== '');
    if (hasActiveFilters) {
      fetchFilteredData().then(mappedItems => {
        const itemsWithHandlers = (mappedItems || []).map((mi: any) => {
          const miId = mi.id ?? mi.ID ?? String(mi.id ?? mi.ID);
          const orig = originalItems.find((o: any) => String(o.id ?? o.ID) === String(miId));
          return {
            ...mi,
            onEditPress: mi.onEditPress || orig?.onEditPress,
            onViewPress: mi.onViewPress || orig?.onViewPress,
            id: String(mi.id ?? mi.ID ?? (orig && orig.id) ?? ''),
            modified_at: formatModifiedDate(mi.modified_at || mi.MODIFIED_AT || ''),
          };
        });
        setFilteredItems(itemsWithHandlers);
      });
    } else {
      // No filters active, show all original items
      setFilteredItems(originalItems);
    }
  }, [filters, fetchFilteredData, originalItems]);

  return (
    <View style={styles.container}>
      {/* Header - matching StandupWelcome layout */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Stand-up</Text>
        <TouchableOpacity style={styles.backButton} onPress={onBackPress}>
          <Text style={styles.backButtonText}>Back</Text>
        </TouchableOpacity>
      </View>
      {/* Stand-up Stats — 2 victory donut charts (by Status + by Type) */}
        <StandupStatsCard items={filteredItems} />
      {/* Cards Container with Filters */}
      <View style={styles.cardsContainer}>
        {/* Filters using LHFilter - matching SparcWalk implementation */}
        <View style={styles.filterContainer}>
          <LHFilter
            filters={filterOptions}
            onFilterChange={handleFilterChange}
            itemsPerRow={4}
            onClearAllFilters={handleResetFilters}
          />
        </View>

        {/* Standup List Container */}
        <View style={styles.listContainer}>
          {isLoading ? (
            <ScrollView 
              style={styles.scrollView}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              {[...Array(10)].map((_, index) => (
                <View key={`skeleton-${index}`} style={index > 0 ? styles.itemWrapper : undefined}>
                  <StandupListItemSkeleton />
                </View>
              ))}
            </ScrollView>
          ) : filteredItems.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No standup cards found</Text>
            </View>
          ) : (
            <ScrollView 
              style={styles.scrollView}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={true}
            >
              {filteredItems.map((item, index) => (
                <View key={item.id} style={index > 0 ? styles.itemWrapper : undefined}>
                  <StandupListItem
                    title={item.title}
                    status={item.status}
                    statusColor={item.statusColor}
                    lastModified={item.modified_at}
                    modifiedBy={item.modified_by}
                    onEditPress={item.onEditPress}
                    onViewPress={item.onViewPress}
                  />
                </View>
              ))}
            </ScrollView>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F9FF',
    paddingVertical: 20,
    paddingHorizontal: Platform.OS === 'web' ? '1.5%' : 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 16,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#212529',
    fontFamily: FontFamily.bold,
    lineHeight: 32,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#034895',
    borderRadius: Platform.OS === 'web' ? 20 : 24,
    height: Platform.OS === 'web' ? 40 : 32,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  cardsContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: 'rgba(0, 0, 0, 0.1)',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 1,
        shadowRadius: 8,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      },
    }),
  },
  filterContainer: {
    // marginTop: 8,
    // marginBottom: 8,
    marginLeft:20,
    marginRight:20,
    marginTop: 40,
    marginBottom: -10,
  },
  listContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
    maxHeight: 832, // Height for exactly 10 items: (10 * 72) + (9 * 8) + 40
  },
  scrollContent: {
    padding: 20,
  },
  itemWrapper: {
    marginTop: 8,
  },
  emptyState: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    color: '#9E9E9E',
    fontFamily: FontFamily.regular,
  },
});

export default StandUpCardsScreen;
